import { atom } from 'jotai';
import dayjs from 'dayjs';
import {
  CHILD_PROVIDER_SITUATION_VALUES,
  FAMILY_APPLICATION_STEPS,
  FAMILY_APPLICATION_STEPS_ORDER,
  FAMILY_COMPONENT_MAP,
  FAMILY_STATUS_MAP,
} from './familyApplicationConstants';
import { EMPLOYMENT_STATUS } from 'constants';
import { ADULT_MARITAL_STATUS_OPTIONS } from './__steps/household/FamilyAppHouseholdStructure';
import {
  spouseBranchRequiresComponent,
  shouldShowSpouseCurrentStatus,
  shouldShowSpouseOrderStartDate,
  shouldShowSpouseOrderEndDate,
} from './__steps/household/FamilyAppSpouseMilitaryEmployment';
import { FAMILY_SPOUSE_UNEMPLOYMENT_REASON } from './__steps/household/constants';

// Feature flags atom - populated from the React component tree via useSetAtom
export const featureFlagsAtom = atom({});

// Application state atom
export const applicationAtom = atom(null);

// Applications list atom (for all applications of the family user)
export const applicationsAtom = atom(null);

// Current step atom
export const currentStepAtom = atom(null);

// Step completions atom
export const stepCompletionsAtom = atom(null);

// Checklist items atom (for submitted application checklist)
export const checklistItemsAtom = atom(null);

// Controls whether the "Have something not listed above?" section is expanded
export const additionalItemsExpandedAtom = atom(false);

// Checklist status banner data from /checklist/status endpoint
export const checklistStatusAtom = atom(null);

// Program details (acknowledgement card) atom
export const programDetailsAtom = atom(null);

// Controls whether the program details card is expanded
export const programDetailsExpandedAtom = atom(false);

// Helper function to check if marital status requires an additional parent
export const requiresAdditionalParent = (application) => {
  const maritalStatus = application?.applicant?.maritalStatus;
  const partnerStatus = application?.applicant?.partnerStatus;

  return (
    [ADULT_MARITAL_STATUS_OPTIONS.MARRIED, ADULT_MARITAL_STATUS_OPTIONS.SEPARATED].includes(maritalStatus) ||
    [
      ADULT_MARITAL_STATUS_OPTIONS.CO_PARENT,
      ADULT_MARITAL_STATUS_OPTIONS.DIVORCED_SHARED_CUSTODY,
      ADULT_MARITAL_STATUS_OPTIONS.POA,
    ].includes(partnerStatus)
  );
};

// Helper function to check if the relationship is Power of Attorney
export const isPowerOfAttorney = (application) => {
  const maritalStatus = application?.applicant?.maritalStatus;
  const partnerStatus = application?.applicant?.partnerStatus;

  return maritalStatus === ADULT_MARITAL_STATUS_OPTIONS.SINGLE && partnerStatus === ADULT_MARITAL_STATUS_OPTIONS.POA;
};

// Helper function to check if child is 4 years or older
export const isChildSchoolAge = (child) => {
  if (!child?.dateOfBirth) return false;
  const ageInYears = dayjs().diff(dayjs(child.dateOfBirth), 'years');
  return ageInYears >= 4;
};

export const doesProviderInfoStepApply = (child) => {
  return [CHILD_PROVIDER_SITUATION_VALUES.InCare, CHILD_PROVIDER_SITUATION_VALUES.ProviderIdentified].includes(
    child?.providerSituation,
  );
};

// Children's summary and documents apply to the entire children section, not per child
export const CHILD_STEPS_EXCLUDED_FROM_ENTITY_INDEX = [
  FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY,
  FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION,
  FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS,
];

export const doesChildCareNeedsStepApply = (child) => {
  return !!child?.providerSituation && child?.providerSituation !== CHILD_PROVIDER_SITUATION_VALUES.InCare;
};

export const doesCareNeedTypeStepApply = (child) => {
  return (
    isChildSchoolAge(child) &&
    child?.inSchool === true &&
    child?.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded
  );
};

export const doesScheduleOfCareStepApply = (child) => {
  return child?.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded;
};

const SCHOOL_BASED_CARE_TYPES = ['BEFORE_SCHOOL', 'AFTER_SCHOOL', 'BEFORE_AND_AFTER_SCHOOL', 'SUMMER'];

export const doesDailyTimeBlocksStepApply = (child) => {
  if (!doesScheduleOfCareStepApply(child)) return false;
  // Non-school children (NON_SCHOOL care type) always have daily time blocks after schedule
  if (!doesCareNeedTypeStepApply(child)) return true;
  // School children need a school-based care type selected first
  const raw = child?.schedules?.[0]?.careNeedType;
  const code = raw?.code ?? raw;
  return SCHOOL_BASED_CARE_TYPES.includes(code);
};

// Helper function to get the last step for a child based on their provider situation
export const getChildLastStep = (child) => {
  if (doesDailyTimeBlocksStepApply(child)) {
    return FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS;
  }

  if (doesScheduleOfCareStepApply(child)) {
    return FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE;
  }

  if (doesCareNeedTypeStepApply(child)) {
    return FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE;
  }

  if (doesChildCareNeedsStepApply(child)) {
    return FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS;
  }

  // If child has a provider (InCare or ProviderIdentified), go to provider info step
  if (doesProviderInfoStepApply(child)) {
    return FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO;
  }

  // If child needs referral help, skip provider info step
  return FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION;
};

// Helper function to check if any child step is completed
export const isChildStepCompleted = (stepId, child) => {
  if (!child) return false;

  switch (stepId) {
    case FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO:
      return !!(
        child?.firstName &&
        child?.lastName &&
        child?.dateOfBirth &&
        child?.relationToApplicant &&
        child?.applicantOnBirthCertificate !== undefined
      );
    case FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION:
      return !!child.livingSituation;
    case FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS:
      return child.inSchool !== undefined && child.inSchool !== null;
    case FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION:
      return !!child.providerSituation;
    case FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO:
      return child.hasProvider === true;
    case FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS:
      return !!child.careStartDate || !!child.careStartDateUnknown;
    case FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE:
      return !!child.schedules?.[0]?.careNeedType;
    case FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE:
      return child.schedules?.length > 0;
    case FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS:
      return (
        child.schedules?.[0]?.days?.some(
          (d) => d.morning?.startTime || d.afternoon?.startTime || d.summer?.startTime || d.nonSchool?.startTime,
        ) || false
      );
    default:
      return false;
  }
};

// Base step configuration
const baseStepConfig = {
  [FAMILY_APPLICATION_STEPS.START]: {
    disabled: false,
    doesApply: true,
    isCompleted: (application) => {
      // Start step is completed when we have an application ID and applicant data
      return !!application?.id && !!application?.applicant?.id;
    },
  },
  [FAMILY_APPLICATION_STEPS.COMPONENT]: {
    doesApply: true,
    isCompleted: (application) => {
      // Component is completed when we have a military component ID
      return !!application?.applicant?.militaryDetails?.militaryComponent?.id;
    },
  },
  [FAMILY_APPLICATION_STEPS.STATUS]: {
    doesApply: (application) => {
      // Only applies if not a civilian
      return (
        application?.applicant?.militaryDetails?.militaryComponent?.id !== FAMILY_COMPONENT_MAP.civilianEmployee.id
      );
    },
    isCompleted: (application) => {
      // Status is completed when we have a military status ID
      return !!application?.applicant?.militaryDetails?.militaryStatus?.id;
    },
  },
  [FAMILY_APPLICATION_STEPS.RANK]: {
    doesApply: (application) => {
      // Only applies if not a civilian
      return application?.applicant?.militaryDetails?.militaryComponent?.id !== 2; // 2 is civilianEmployee.id
    },
    isCompleted: (application) => {
      // Rank is completed when we have a grade ID
      return (
        !!application?.applicant?.militaryDetails?.grade?.id || !!application?.applicant?.militaryDetails?.gradeUnknown
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.MARITAL_STATUS]: {
    doesApply: true,
    isCompleted: (application) => {
      // Marital status is completed when we have marital status set
      return !!application?.applicant?.maritalStatus;
    },
  },
  [FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT]: {
    doesApply: (application) => {
      // Only applies if component is civilian OR status is dual-status technician currently civilian
      return (
        application?.applicant?.militaryDetails?.militaryComponent?.id === FAMILY_COMPONENT_MAP.civilianEmployee.id ||
        application?.applicant?.militaryDetails?.militaryStatus?.id ===
          FAMILY_STATUS_MAP.dualStatusTechnicianCurrentlyCivilian.id
      );
    },
    isCompleted: (application) => {
      // Civilian employment is completed when we have a payment system ID
      return (
        !!application?.applicant?.militaryDetails?.paymentSystem?.id ||
        !!application?.applicant?.militaryDetails?.paymentSystemUnknown
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION]: {
    doesApply: true,
    isCompleted: (application) => {
      return typeof application?.applicant?.homeAddress?.id === 'number';
    },
  },
  [FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION]: {
    doesApply: true,
    isCompleted: (application) => {
      // Contact info is completed when we have an email
      return !!application?.applicant?.email;
    },
  },
  [FAMILY_APPLICATION_STEPS.DOCUMENTS]: {
    doesApply: true,
    isCompleted: () => true,
    isInformational: true,
  },
  [FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE]: {
    doesApply: (application) => {
      // Only applies if we need additional parent info
      return requiresAdditionalParent(application);
    },
    isCompleted: (application) => {
      // Household structure is completed when we have additional parent data
      return !!application?.additionalParents?.[0]?.firstName && !!application?.additionalParents?.[0]?.lastName;
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS]: {
    doesApply: (application) => {
      // Only applies if applicant has marital status that requires additional parent
      return requiresAdditionalParent(application);
    },
    isCompleted: (application) => {
      // Residence status is completed when we have addressSameAsApplicant set
      return (
        application?.additionalParents?.[0]?.addressSameAsApplicant !== undefined &&
        application?.additionalParents?.[0]?.addressSameAsApplicant !== null
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES]: {
    doesApply: (application) => {
      return requiresAdditionalParent(application) && !isPowerOfAttorney(application);
    },
    isCompleted: (application) => {
      // Employment status is completed when we have employmentStatus set
      return (
        application?.additionalParents?.[0]?.employmentStatus !== undefined &&
        application?.additionalParents?.[0]?.employmentStatus !== null
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE]: {
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      return (
        hasAdditionalParent &&
        !isPowerOfAttorney(application) &&
        application?.additionalParents?.[0]?.employmentStatus === EMPLOYMENT_STATUS.EMPLOYED
      );
    },
    isCompleted: (application) => {
      // Employment type is completed when we have fullTimeEmployee, selfEmployed, or militaryEmployee set
      return (
        application?.additionalParents?.[0]?.fullTimeEmployee !== undefined ||
        application?.additionalParents?.[0]?.selfEmployed !== undefined ||
        application?.additionalParents?.[0]?.militaryEmployee !== undefined
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS]: {
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      return (
        hasAdditionalParent &&
        !isPowerOfAttorney(application) &&
        application?.additionalParents?.[0]?.employmentStatus === EMPLOYMENT_STATUS.EMPLOYED &&
        application?.additionalParents?.[0]?.fullTimeEmployee === true
      );
    },
    isCompleted: (application) => {
      const spouse = application?.additionalParents?.[0] || {};
      return (
        spouse?.employmentDetails?.payStubsAvailable !== null &&
        spouse?.employmentDetails?.payStubsAvailable !== undefined &&
        typeof spouse?.employmentDetails?.hoursPerWeek === 'number' &&
        typeof spouse?.employmentDetails?.employerCount === 'number'
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT]: {
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      return (
        hasAdditionalParent &&
        !isPowerOfAttorney(application) &&
        application?.additionalParents?.[0]?.employmentStatus === EMPLOYMENT_STATUS.EMPLOYED &&
        application?.additionalParents?.[0]?.selfEmployed === true
      );
    },
    isCompleted: (application) => {
      const spouse = application?.additionalParents?.[0] || {};
      const selfEmploymentDetails = spouse?.selfEmploymentDetails || {};
      return (
        selfEmploymentDetails.type !== undefined &&
        selfEmploymentDetails.taxDocsAvailable !== undefined &&
        typeof selfEmploymentDetails.hoursPerWeek === 'number' &&
        typeof selfEmploymentDetails.businessCount === 'number'
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT]: {
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      return (
        hasAdditionalParent &&
        !isPowerOfAttorney(application) &&
        application?.additionalParents?.[0]?.employmentStatus === EMPLOYMENT_STATUS.EMPLOYED &&
        application?.additionalParents?.[0]?.militaryEmployee === true
      );
    },
    isCompleted: (application) => {
      const spouse = application?.additionalParents?.[0];
      if (!spouse?.militaryDetails) return false;

      const { militaryDetails } = spouse;

      // Required fields
      if (!militaryDetails.hoursPerWeek || !militaryDetails.branch) return false;

      // Component required for certain branches
      if (spouseBranchRequiresComponent(militaryDetails.branch?.id) && !militaryDetails.militaryComponent) {
        return false;
      }

      // Status required if component is not civilian
      const shouldShowStatus = shouldShowSpouseCurrentStatus({
        branch: militaryDetails.branch?.title,
        componentId: militaryDetails.militaryComponent?.id,
      });
      if (shouldShowStatus && !militaryDetails.militaryStatus) return false;

      // Order start date required for certain statuses
      if (shouldShowSpouseOrderStartDate({ statusId: militaryDetails.militaryStatus?.id })) {
        if (!militaryDetails.orderStartDate && !militaryDetails.orderStartDateUnknown) return false;
      }

      // Order end date required for certain statuses
      if (shouldShowSpouseOrderEndDate({ statusId: militaryDetails.militaryStatus?.id })) {
        if (!militaryDetails.orderEndDate && !militaryDetails.orderEndDateUnknown) return false;
      }

      return true;
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH]: {
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      const totalWeeklyHours = application?.eligibilitySummary?.totalWeeklyHours;
      const isNonEmployed = application?.additionalParents?.[0]?.employmentStatus === EMPLOYMENT_STATUS.NONEMPLOYED;
      const meetsHourRequirement = totalWeeklyHours !== undefined && totalWeeklyHours < 16;

      return (
        hasAdditionalParent &&
        !isPowerOfAttorney(application) &&
        (isNonEmployed || meetsHourRequirement) &&
        application?.additionalParents?.[0]?.reasonForUnemployment === FAMILY_SPOUSE_UNEMPLOYMENT_REASON.JobSearch
      );
    },
    isCompleted: (application) => {
      const spouse = application?.additionalParents?.[0];
      if (!spouse) return false;

      return (
        spouse.unemploymentDetails?.unemployedSince &&
        spouse.unemploymentDetails?.fullTimeCareNeeded !== undefined &&
        spouse.unemploymentDetails?.certificationAccepted === true &&
        spouse.unemploymentDetails?.certificationSigned === true
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON]: {
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      const totalWeeklyHours = application?.eligibilitySummary?.totalWeeklyHours;
      const isNonEmployed = application?.additionalParents?.[0]?.employmentStatus === EMPLOYMENT_STATUS.NONEMPLOYED;
      const meetsHourRequirement = totalWeeklyHours !== undefined && totalWeeklyHours < 16;
      const isNotEligible = application?.eligibilitySummary?.eligibility === 'NotEligible';

      return (
        isNotEligible &&
        hasAdditionalParent &&
        !isPowerOfAttorney(application) &&
        (isNonEmployed || meetsHourRequirement)
      );
    },
    isCompleted: (application) => {
      // Unemployment reason is completed when we have reasonForUnemployment set
      return (
        application?.additionalParents?.[0]?.reasonForUnemployment !== undefined &&
        application?.additionalParents?.[0]?.reasonForUnemployment !== null
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION]: {
    doesApply: (application) => {
      if (!requiresAdditionalParent(application) || isPowerOfAttorney(application)) {
        return false;
      }

      // Skip education page if employed and total hours >= 25
      const spouse = application?.additionalParents?.[0];
      if (spouse?.employmentStatus === EMPLOYMENT_STATUS.EMPLOYED) {
        const employmentHours = spouse?.employmentDetails?.hoursPerWeek || 0;
        const selfEmploymentHours = spouse?.selfEmploymentDetails?.hoursPerWeek || 0;
        const militaryHours = spouse?.militaryDetails?.hoursPerWeek || 0;
        const totalHours = employmentHours + selfEmploymentHours + militaryHours;

        if (totalHours >= 25) {
          return false;
        }
      }

      return true;
    },
    isCompleted: (application) => {
      // Education step is completed when we have inSchool flag set
      return (
        application?.additionalParents?.[0]?.inSchool !== undefined &&
        application?.additionalParents?.[0]?.inSchool !== null
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY]: {
    doesApply: (application) => {
      return requiresAdditionalParent(application) && !isPowerOfAttorney(application);
    },
    isCompleted: () => true, // Always complete once viewed
    isInformational: true,
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO]: {
    // Only applies if applicant has marital status that requires additional parent AND there is a spouse
    doesApply: (application) => {
      const hasAdditionalParent = requiresAdditionalParent(application);
      return hasAdditionalParent && !!application?.additionalParents?.[0]?.id;
    },
    isCompleted: (application) => {
      // Contact info is completed when we have an email
      return (
        !!application?.additionalParents?.[0]?.email ||
        (application?.completedBySponsor === true && application?.additionalParents?.[0]?.contactInfoWithheld === true)
      );
    },
  },
  [FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS]: {
    doesApply: (application) => {
      return requiresAdditionalParent(application);
    },
    isCompleted: () => true,
    isInformational: true,
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO]: {
    doesApply: true,
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION]: {
    doesApply: true,
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS]: {
    doesApply: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];
      return isChildSchoolAge(currentChild);
    },
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION]: {
    doesApply: true,
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO]: {
    doesApply: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];

      // Only show this step if user selected "InCare" or "ProviderIdentified"
      return doesProviderInfoStepApply(currentChild);
    },
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];

      return currentChild?.hasProvider === true;
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS]: {
    doesApply: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];

      return doesChildCareNeedsStepApply(currentChild);
    },
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex || 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE]: {
    doesApply: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex ?? 0];
      return doesCareNeedTypeStepApply(currentChild);
    },
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex ?? 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE]: {
    doesApply: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex ?? 0];
      return doesScheduleOfCareStepApply(currentChild);
    },
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex ?? 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS]: {
    doesApply: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex ?? 0];
      return doesDailyTimeBlocksStepApply(currentChild);
    },
    isCompleted: (application, entityIndex) => {
      const children = application?.children || [];
      const currentChild = children[entityIndex ?? 0];
      return isChildStepCompleted(FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS, currentChild);
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY]: {
    doesApply: (application) => {
      // Only applies if there are children
      return !!(application?.children?.length > 0);
    },
    isCompleted: (application, entityIndex) => {
      // Summary is always considered complete once you can access it
      return true;
    },
    isInformational: true,
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION]: {
    doesApply: (application) => {
      // Only applies if there are children
      return !!(application?.children?.length > 0);
    },
    isCompleted: (application) => {
      // Completed when self-certification is accepted and signed
      return !!application?.selfcertAccepted && !!application?.selfcertAcceptedBy;
    },
  },
  [FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS]: {
    doesApply: (application) => {
      // Only applies if there are children
      return !!(application?.children?.length > 0);
    },
    isCompleted: (application, entityIndex) => {
      // Always complete once viewed
      return true;
    },
    isInformational: true,
  },
  [FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS]: {
    doesApply: true,
    isCompleted: () => true, // Always complete once viewed
    isInformational: true,
  },
  [FAMILY_APPLICATION_STEPS.AGREEMENT]: {
    doesApply: true,
    isCompleted: (application) => {
      // Agreement is completed when user has accepted the agreement
      return !!application?.agreementAccepted;
    },
  },
  [FAMILY_APPLICATION_STEPS.SUBMITTED]: {
    doesApply: true,
    isCompleted: (application) => {
      // Setting this to the same as agreement step for now
      return !!application?.agreementAccepted;
    },
  },
};

// Helper function to get the next applicable step
export const getNextStep = (currentStep, application, stepConfig, entityIndex = null) => {
  // Special handling for children steps with multi-child flow
  if (entityIndex !== null && entityIndex !== undefined) {
    const children = application?.children || [];
    const currentChildIndex = entityIndex;
    const currentChild = children[currentChildIndex];

    // If we're on the last children step for this child and there are more children, go to first step of next child
    const childLastStep = getChildLastStep(currentChild);
    if (currentStep === childLastStep) {
      const nextChildIndex = currentChildIndex + 1;

      if (nextChildIndex < children.length) {
        // There's another child, go to their basic info step
        return `${FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO}?entity=${nextChildIndex}`;
      }

      // No more children, go directly to children summary (don't use normal flow)
      return FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY;
    }
  }

  // Special case: If we're on the last child's last step and entityIndex is null,
  // this means we should go to children summary
  if ((entityIndex === null || entityIndex === undefined) && application?.children?.length > 0) {
    const lastChild = application.children[application.children.length - 1];
    const lastChildLastStep = getChildLastStep(lastChild);

    if (currentStep === lastChildLastStep) {
      return FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY;
    }
  }

  const currentStepIndex = FAMILY_APPLICATION_STEPS_ORDER.indexOf(currentStep);
  if (currentStepIndex === -1 || currentStepIndex === FAMILY_APPLICATION_STEPS_ORDER.length - 1)
    return FAMILY_APPLICATION_STEPS.START;

  for (let i = currentStepIndex + 1; i < FAMILY_APPLICATION_STEPS_ORDER.length; i++) {
    const stepId = FAMILY_APPLICATION_STEPS_ORDER[i];
    const config = stepConfig?.[stepId] || baseStepConfig[stepId];
    if (!config) continue;

    let doesApply = false;
    if (config.doesApply === true) {
      doesApply = true;
    } else if (typeof config.doesApply === 'function') {
      // Pass entityIndex for children steps that need it
      if (entityIndex !== null && entityIndex !== undefined) {
        doesApply = config.doesApply(application, entityIndex);
      } else {
        doesApply = config.doesApply(application);
      }
    }

    if (doesApply) {
      if (entityIndex !== null && entityIndex !== undefined) {
        return `${stepId}?entity=${entityIndex}`;
      }
      return stepId;
    }
  }

  return FAMILY_APPLICATION_STEPS.START;
};

// Helper function to get the previous applicable step
export const getPreviousStep = (currentStep, application, stepConfig, entityIndex = null) => {
  const currentStepIndex = FAMILY_APPLICATION_STEPS_ORDER.indexOf(currentStep);
  if (currentStepIndex <= 0) return null;

  for (let i = currentStepIndex - 1; i >= 0; i--) {
    const stepId = FAMILY_APPLICATION_STEPS_ORDER[i];
    const config = stepConfig?.[stepId];
    if (!config) continue;

    let doesApply = false;
    if (config.doesApply === true) {
      doesApply = true;
    } else if (typeof config.doesApply === 'function') {
      // Pass entityIndex for children steps that need it
      if (entityIndex !== null && entityIndex !== undefined && stepId.startsWith('children-')) {
        doesApply = config.doesApply(application, entityIndex);
      } else {
        doesApply = config.doesApply(application);
      }
    }

    if (doesApply) {
      if (entityIndex !== null && entityIndex !== undefined) {
        return `${stepId}?entity=${entityIndex}`;
      }
      return stepId;
    }
  }

  return FAMILY_APPLICATION_STEPS.START;
};

// Helper function to check if ALL previous required steps are completed
const areAllPreviousStepsCompleted = (
  currentStep,
  application,
  stepConfig,
  entityIndex = null,
  stepCompletions = [],
) => {
  // Helper function to check if a step is completed for a specific entity
  const _checkStepCompletion = (stepId, config, application, entityIndex, stepCompletions) => {
    let doesApply =
      config.doesApply === true ||
      (typeof config.doesApply === 'function' ? config.doesApply(application, entityIndex) : false);

    if (doesApply) {
      let isCompleted = false;

      // For informational steps, check stepCompletions database
      if (config.isInformational) {
        const stepKey = getStepKey(stepId, entityIndex);
        const completion = stepCompletions?.find((s) => s.stepKey === stepKey);
        isCompleted = completion?.completed || false;
      } else {
        // For data-based steps, use the isCompleted function
        isCompleted = typeof config.isCompleted === 'function' ? config.isCompleted(application, entityIndex) : false;
      }

      if (!isCompleted) return false; // If any required step is incomplete, return false
    }
    return true;
  };

  // Build expanded step order that includes all children
  const expandedStepOrder = [];
  const childrenCount = application?.children?.length || 0;

  // Collect all child step IDs first
  const childStepIds = [];
  const nonChildSteps = [];

  for (const stepId of FAMILY_APPLICATION_STEPS_ORDER) {
    if (stepId?.startsWith('children-') && !CHILD_STEPS_EXCLUDED_FROM_ENTITY_INDEX.includes(stepId)) {
      childStepIds.push(stepId);
    } else {
      nonChildSteps.push({ stepId, isChildStep: false });
    }
  }

  // Build the expanded order by going through non-child steps
  // and expanding child steps for each child when we encounter the first child step
  let childStepsExpanded = false;

  for (const step of nonChildSteps) {
    // If this is the first child step position and we haven't expanded yet
    const childStepsStartIndex = FAMILY_APPLICATION_STEPS_ORDER.indexOf(FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO);
    const currentStepIndex = FAMILY_APPLICATION_STEPS_ORDER.indexOf(step.stepId);

    // Add all steps before children section
    if (currentStepIndex < childStepsStartIndex) {
      expandedStepOrder.push({ stepId: step.stepId, entityIndex: null });
    } else if (!childStepsExpanded) {
      // We've reached the children section - expand all child steps for all children
      // Always include at least child 0's steps since every application requires at least 1 child
      const effectiveChildrenCount = Math.max(childrenCount, 1);
      for (let childIndex = 0; childIndex < effectiveChildrenCount; childIndex++) {
        for (const childStepId of childStepIds) {
          expandedStepOrder.push({ stepId: childStepId, entityIndex: childIndex });
        }
      }
      childStepsExpanded = true;

      // Then add this non-child step (like children-summary, documentation, etc.)
      expandedStepOrder.push({ stepId: step.stepId, entityIndex: null });
    } else {
      // After children section
      expandedStepOrder.push({ stepId: step.stepId, entityIndex: null });
    }
  }

  // Find the current step in the expanded order
  let currentStepIndex = -1;
  for (let i = 0; i < expandedStepOrder.length; i++) {
    const step = expandedStepOrder[i];
    if (step.stepId === currentStep && step.entityIndex === entityIndex) {
      currentStepIndex = i;
      break;
    }
  }

  // Special case: if checking a child step (entity=0) but no children exist yet,
  // we still need to validate all previous non-child steps are completed
  if (currentStepIndex === -1 && currentStep.startsWith('children-') && entityIndex === 0 && childrenCount === 0) {
    // Check all steps before the children section
    const childStepsStartIndex = FAMILY_APPLICATION_STEPS_ORDER.indexOf(FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO);

    for (let i = 0; i < childStepsStartIndex; i++) {
      const stepId = FAMILY_APPLICATION_STEPS_ORDER[i];
      const config = stepConfig[stepId];
      if (!config) continue;

      const isStepCompleted = _checkStepCompletion(stepId, config, application, null, stepCompletions);

      if (!isStepCompleted) {
        // Any incomplete step blocks the first child step
        return false;
      }
    }

    // All previous steps completed, allow access to first child step
    return true;
  }

  if (currentStepIndex <= 0) return true; // First step or not found

  // Check ALL previous steps in the expanded order
  // If ANY step is incomplete, all steps after it are disabled
  for (let i = 0; i < currentStepIndex; i++) {
    const step = expandedStepOrder[i];
    const config = stepConfig[step.stepId];
    if (!config) continue;

    // Check if this step is completed for the specific entity
    const isStepCompleted = _checkStepCompletion(step.stepId, config, application, step.entityIndex, stepCompletions);

    if (!isStepCompleted) {
      // Any incomplete step blocks all subsequent steps
      return false;
    }
  }

  return true; // All previous required steps are completed
};

// Helper function to generate step key with entity index for children steps
export const getStepKey = (stepId, entityIndex = null) => {
  if (
    entityIndex !== null &&
    stepId?.startsWith('children-') &&
    !CHILD_STEPS_EXCLUDED_FROM_ENTITY_INDEX.includes(stepId)
  ) {
    return `${stepId}?entity=${entityIndex}`;
  }
  return stepId;
};

// Step configuration atom - stores metadata about each step
export const stepConfigAtom = atom((get) => {
  const stepCompletions = get(stepCompletionsAtom);
  const featureFlags = get(featureFlagsAtom);

  // Don't return step config until step completions are loaded
  if (stepCompletions === null) {
    return null;
  }

  return {
    [FAMILY_APPLICATION_STEPS.START]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.START],
    },
    [FAMILY_APPLICATION_STEPS.COMPONENT]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.COMPONENT],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.COMPONENT,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.STATUS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.STATUS],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(FAMILY_APPLICATION_STEPS.STATUS, application, stepConfig, null, stepCompletions),
    },
    [FAMILY_APPLICATION_STEPS.RANK]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.RANK],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(FAMILY_APPLICATION_STEPS.RANK, application, stepConfig, null, stepCompletions),
    },
    [FAMILY_APPLICATION_STEPS.MARITAL_STATUS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.MARITAL_STATUS],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.MARITAL_STATUS,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.DOCUMENTS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.DOCUMENTS],
      isCompleted: (application) => {
        return stepCompletions?.some((s) => s.stepKey === FAMILY_APPLICATION_STEPS.DOCUMENTS && s.completed);
      },
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.DOCUMENTS,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY],
      isCompleted: (application) => {
        return stepCompletions?.some((s) => s.stepKey === FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY && s.completed);
      },
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS],
      isCompleted: (application) => {
        return stepCompletions?.some(
          (s) => s.stepKey === FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS && s.completed,
        );
      },
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE],
      doesApply: (application, entityIndex) => {
        if (!featureFlags.showFamilyApplicationsScheduleOfCare) return false;
        return baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE].doesApply(application, entityIndex);
      },
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE],
      doesApply: (application, entityIndex) => {
        if (!featureFlags.showFamilyApplicationsScheduleOfCare) return false;
        return baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE].doesApply(application, entityIndex);
      },
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS],
      doesApply: (application, entityIndex) => {
        if (!featureFlags.showFamilyApplicationsScheduleOfCare) return false;
        return baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS].doesApply(application, entityIndex);
      },
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY],
      isCompleted: (application) => {
        return stepCompletions?.some((s) => s.stepKey === FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY && s.completed);
      },
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION],
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS],
      isCompleted: (application) => {
        return stepCompletions?.some((s) => s.stepKey === FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS && s.completed);
      },
      disabled: (application, stepConfig, entityIndex = null) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS,
          application,
          stepConfig,
          entityIndex,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS],
      isCompleted: (application) => {
        return stepCompletions?.some(
          (s) => s.stepKey === FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS && s.completed,
        );
      },
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.AGREEMENT]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.AGREEMENT],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.AGREEMENT,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
    [FAMILY_APPLICATION_STEPS.SUBMITTED]: {
      ...baseStepConfig[FAMILY_APPLICATION_STEPS.SUBMITTED],
      disabled: (application, stepConfig) =>
        !areAllPreviousStepsCompleted(
          FAMILY_APPLICATION_STEPS.SUBMITTED,
          application,
          stepConfig,
          null,
          stepCompletions,
        ),
    },
  };
});

// Helper function to get the next incomplete step
export const getNextIncompleteStep = (application, stepConfig, entityIndex = null) => {
  const steps = Object.entries(stepConfig);

  for (const [stepId, config] of steps) {
    // Only pass entityIndex for children steps
    const doesApply =
      config.doesApply === true ||
      (typeof config.doesApply === 'function'
        ? entityIndex !== null && entityIndex !== undefined && stepId.startsWith('children-')
          ? config.doesApply(application, entityIndex)
          : config.doesApply(application)
        : false);

    const isCompleted =
      typeof config.isCompleted === 'function'
        ? entityIndex !== null && entityIndex !== undefined && stepId.startsWith('children-')
          ? config.isCompleted(application, entityIndex)
          : config.isCompleted(application)
        : false;

    if (doesApply && !isCompleted) {
      return stepId;
    }
  }

  return null;
};

// Helper function to get the latest available step (furthest step with no disabled steps before it)
export const getLatestAvailableStep = (application, stepConfig) => {
  if (application?.submittedDate) {
    return FAMILY_APPLICATION_STEPS.SUBMITTED;
  }

  let latestAvailableStep = FAMILY_APPLICATION_STEPS.START;

  // Go through steps in order and find the latest one that's available
  for (const stepId of FAMILY_APPLICATION_STEPS_ORDER) {
    const config = stepConfig?.[stepId];
    if (!config) continue;

    // For children steps that require entity index, find the latest available child step
    if (stepId.startsWith('children-') && !CHILD_STEPS_EXCLUDED_FROM_ENTITY_INDEX.includes(stepId)) {
      const childrenCount = application?.children?.length || 0;
      let foundAvailableChildStep = false;

      // Check each child to find the latest available step
      for (let childIndex = 0; childIndex < childrenCount; childIndex++) {
        const doesApply =
          config.doesApply === true ||
          (typeof config.doesApply === 'function' ? config.doesApply(application, childIndex) : false);

        if (!doesApply) continue;

        const isDisabled =
          typeof config.disabled === 'function'
            ? config.disabled(application, stepConfig, childIndex)
            : config.disabled;

        if (!isDisabled) {
          // only set the latest available step if the current value for it
          // is not for a child with a higher index
          // Get the index from the latest available step
          const latestAvailableStepIndex = latestAvailableStep.split('?entity=')[1];
          if (
            latestAvailableStepIndex === undefined ||
            latestAvailableStepIndex === null ||
            childIndex >= parseInt(latestAvailableStepIndex)
          ) {
            latestAvailableStep = `${stepId}?entity=${childIndex}`;
          }

          foundAvailableChildStep = true;
        } else {
          // If this child's step is disabled
          // check if latestAvailableStep is set to a higher index
          // and lower it if needed
          const [latestAvailableStepId, latestAvailableStepIndex] = latestAvailableStep.split('?entity=');
          if (latestAvailableStepIndex > childIndex) {
            latestAvailableStep = `${latestAvailableStepId}?entity=${childIndex}`;
          }

          break;
        }
      }

      // If no child step was available, we can't go further
      if (!foundAvailableChildStep) {
        break;
      }
    } else {
      // Regular step checking (non-entity steps)
      const doesApply =
        config.doesApply === true || (typeof config.doesApply === 'function' ? config.doesApply(application) : false);

      if (!doesApply) continue;

      const isDisabled =
        typeof config.disabled === 'function' ? config.disabled(application, stepConfig) : config.disabled;

      if (!isDisabled) {
        latestAvailableStep = stepId;
      } else {
        // If we hit a disabled step, we can't go further
        break;
      }
    }
  }

  return latestAvailableStep;
};
